import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, KeyRound, Trash2, Pencil } from 'lucide-react';
import EditUserDialog from './EditUserDialog';
import LoginAsUserButton from './LoginAsUserButton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';
import { formatApiErrorDetail } from '../../lib/apiError';

const API = `${API_BASE}/api`;

export default function UserAccountActions({ user, token, currentUserId, onRemoved, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [mode, setMode] = useState(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => { setMode(null); setValue(''); };

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/${user.user_id}/send-invite`, {}, { headers });
      if (data.invite_sent) toast.success(`Invite emailed to ${user.email}`);
      else { setValue(data.link); setMode('link'); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'reset') {
        await axios.post(`${API}/admin/users/${user.user_id}/reset-password`, { new_password: value }, { headers });
        toast.success(`Password reset for ${user.email}. Share it with them securely.`);
      } else {
        await axios.delete(`${API}/admin/users/${user.user_id}`, { headers, data: { confirm_email: value } });
        toast.success(`${user.email} was permanently removed`);
        onRemoved?.();
      }
      close();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const btn = 'p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-40';
  const isSelf = user.user_id === currentUserId;
  return (
    <>
      <LoginAsUserButton target={user} />
      <button onClick={() => setEditing(true)} className={btn} title="Edit name, email or password" data-testid={`edit-user-${user.email}`}><Pencil className="h-4 w-4" /></button>
      <EditUserDialog user={user} open={editing} onOpenChange={setEditing} token={token} onSaved={onSaved} />
      <button onClick={invite} disabled={busy} className={btn} title="Resend set-password invite" data-testid={`resend-invite-${user.email}`}><Mail className="h-4 w-4" /></button>
      <button onClick={() => setMode('reset')} className={btn} title="Reset password" data-testid={`reset-password-${user.email}`}><KeyRound className="h-4 w-4" /></button>
      {!isSelf && <button onClick={() => setMode('remove')} className={`${btn} hover:text-red-400 hover:bg-red-500/10`} title="Remove account" data-testid={`remove-account-${user.email}`}><Trash2 className="h-4 w-4" /></button>}

      <Dialog open={!!mode} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid={`${mode}-user-dialog`}>
          <DialogHeader>
            <DialogTitle>{mode === 'link' ? 'Share set-password link' : mode === 'reset' ? 'Reset password' : 'Remove account permanently'}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {mode === 'link' ? `The invite email couldn't be sent. Share this one-time link with ${user.email}. It expires in 7 days.`
                : mode === 'reset'
                ? `Set a new password for ${user.email}. They can sign in with it straight away.`
                : `This deletes ${user.full_name || user.email} and all their enrolments, progress, submissions, badges and certificates. This cannot be undone. Type their email to confirm.`}
            </DialogDescription>
          </DialogHeader>
          <Input readOnly={mode === 'link'} onFocus={(e) => mode === 'link' && e.target.select()} type={mode === 'remove' ? 'email' : 'text'} value={value} onChange={(e) => setValue(e.target.value)}
            placeholder={mode === 'reset' ? 'New password (min 8 characters)' : user.email}
            className="bg-zinc-950 border-zinc-700" data-testid={`${mode}-user-input`} />
          <DialogFooter>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            {mode === 'link' ? (
              <Button onClick={() => { navigator.clipboard?.writeText(value).then(() => toast.success('Link copied'), () => toast.error('Copy failed. Select the link and copy it manually')); }}
                className="bg-teal-500 hover:bg-teal-400 text-black" data-testid="link-user-copy">Copy link</Button>
            ) : (
            <Button onClick={submit} data-testid={`${mode}-user-confirm`}
              disabled={busy || (mode === 'reset' ? value.length < 8 : value.trim().toLowerCase() !== user.email.toLowerCase())}
              className={mode === 'remove' ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-teal-500 hover:bg-teal-400 text-black'}>
              {mode === 'reset' ? 'Reset password' : 'Remove permanently'}
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
